import React, { useState } from 'react';
import { format } from 'date-fns';

import Button from '../../library/button/button';
import Carousel, { Slide } from '../../library/carousel/carousel';
import Image from '../../library/image/image';
import Link from '../../library/link/link';
import Typography from '../../library/typography/typography';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import { CmsPromotionCarouselItemComponent } from '../../shared/types/graphql-types';
import {
  PROMO_AMOUNT,
  PROMO_CODE,
  PROMO_CREATIVE,
  PROMO_NAME,
  PROMO_POSITION
} from '../../shared/constants/data-layer';

import styles from './cms-promotion-carousel.scss';

interface CmsPromotionCarouselProps {
  cmsComponents: CmsPromotionCarouselItemComponent[];
}

function CmsPromotionCarousel(props: CmsPromotionCarouselProps) {
  const { cmsComponents } = props;

  const [slideIndex, setSlideIndex] = useState(0);

  function onSlideChange(slideIndex) {
    setSlideIndex(slideIndex);
  }

  function trackPromotion(promo, position, clickType) {
    const { code, image } = promo || {};
    const { altText, url } = image || {};
    const [amount] = altText?.match(/\$?\d+%?/) || [];

    trackAction('promotion_select', {
      [PROMO_AMOUNT]: amount,
      [PROMO_CODE]: code,
      [PROMO_CREATIVE]: url,
      [PROMO_NAME]: altText ? `${altText} | ${clickType}` : clickType,
      [PROMO_POSITION]: position
    });
  }

  return (
    <div className={styles['container']} data-testid="cms-promotion-carousel">
      <div className={styles['carousel-wrapper']}>
        {isArrayWithLength(cmsComponents) && (
          <Carousel
            onSlideChange={onSlideChange}
            showIndicators
            slideIndex={slideIndex}
          >
            {cmsComponents.map((promo, idx) => (
              <Slide
                className={styles['slide']}
                key={`cfp-${idx}`}
                shouldLazyLoad
              >
                <Link
                  className={styles['promo-link']}
                  onClick={() => {
                    trackPromotion(promo, idx, 'image');
                  }}
                  to={promo.seeDetailsLink?.href || ''}
                >
                  <Image
                    alt={promo.image?.altText || `${promo.name} promo image`}
                    height="100"
                    src={promo.image?.url || ''}
                    width="300"
                  />
                </Link>

                <div className={styles['carousel-content']}>
                  <Typography
                    className={styles['offer-description']}
                    weight="black"
                  >
                    <span data-testid="cms-promotion-carousel_offer-description">
                      {promo.name}
                    </span>
                    {promo.endDate && (
                      <span
                        className={styles['offer-expiration']}
                        data-testid="cms-promotion-carousel_offer-exp"
                      >
                        Expires {format(promo.endDate, 'MM/DD/YYYY')}
                      </span>
                    )}
                  </Typography>

                  <Button
                    className={styles['see-products-button']}
                    href={
                      promo.seeProductsLink?.href
                        ? promo.seeProductsLink.href
                        : promo.seeDetailsLink?.href || ''
                    }
                    isPrimary
                    onClick={() => {
                      trackPromotion(promo, idx, 'button');
                    }}
                  >
                    {promo.seeProductsLink?.text
                      ? promo.seeProductsLink.text
                      : promo.seeDetailsLink?.text}
                  </Button>

                  {promo.seeProductsLink?.href &&
                    promo.seeDetailsLink?.href && (
                      <Button
                        className={styles['see-details-button']}
                        href={promo.seeDetailsLink.href}
                        onClick={() => {
                          trackPromotion(promo, idx, 'tertiary');
                        }}
                      >
                        {promo.seeDetailsLink.text}
                      </Button>
                    )}
                </div>
              </Slide>
            ))}
          </Carousel>
        )}
      </div>
    </div>
  );
}

export default CmsPromotionCarousel;
